import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PostCard } from "@/components/PostCard";
import { Input } from "@/components/ui/input";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => api.get(`/posts/${id}`).then(({ data }) => setPost(data)).catch(() => setPost(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (post === null) return <p className="text-muted-foreground animate-pulse text-center py-10">Loading post…</p>;
  if (post === false) return <p className="text-center py-10">Post not found.</p>;

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await api.post(`/posts/${id}/comments`, { content: comment });
      setComment("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="post-detail-page">
      <button data-testid="post-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <PostCard post={post} onChange={load} />
      <div className="t-card p-5">
        <h2 className="font-display font-bold text-sm mb-4">Comments ({post.comments?.length || 0})</h2>
        <div className="space-y-4">
          {post.comments?.map((c) => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center text-xs font-bold shrink-0">{c.author?.name?.[0]}</div>
              <div>
                <p className="text-sm"><span className="font-bold">{c.author?.name}</span> <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span></p>
                <p className="text-sm mt-0.5">{c.content}</p>
              </div>
            </div>
          ))}
          {!post.comments?.length && <p className="text-sm text-muted-foreground" data-testid="no-comments">No comments yet. Say something nice.</p>}
        </div>
        <form onSubmit={submit} className="flex gap-2 mt-5">
          <Input data-testid="comment-input" placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} className="rounded-full" />
          <button data-testid="comment-submit-btn" type="submit" className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0 hover:-translate-y-0.5 transition-transform duration-200">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
